'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useAnchoredMenuPosition } from '../../hooks/useAnchoredMenuPosition'
import { useUserMenuKeyboard } from './useUserMenuKeyboard'
import ReactDOM from 'react-dom'

import { Box } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import {
  userMenuDividerStyles,
  userMenuHeaderStyles,
  userMenuSurfaceStyles,
} from './UserMenu.styles'
import type { UserMenuItem } from './UserMenu.types'
import { UserMenuRow } from './UserMenuRow'

export type { UserMenuItem }

export type UserMenuProps = {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  /**
   * Render prop for the element the menu hangs off. `onClick` toggles the
   * menu and `isOpen` lets the trigger reflect that visually.
   *
   * The two aria attributes are supplied rather than left to the consumer:
   * they are the menu's own contract, and they must land on whichever
   * element actually takes focus. Spread them there — without them a screen
   * reader announces a plain button and never learns the menu exists, let
   * alone that it opened.
   */
  trigger: (props: {
    onClick: () => void
    /**
     * Toggles on Enter and Space. A native `<button>` does this by itself,
     * but a div carrying button semantics does not — and that is what the
     * side nav's account row is. Without this the package would hand out a
     * trigger that focuses but cannot be operated from the keyboard.
     */
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void
    isOpen: boolean
    'aria-haspopup': 'menu'
    'aria-expanded': boolean
  }) => React.ReactNode
  /**
   * Content above the divider — typically the operator's avatar, name and
   * role. Supplied by the consumer so the package stays free of identity
   * concerns.
   */
  header?: React.ReactNode
  items: UserMenuItem[]
  colorScheme?: ColorScheme
  /**
   * Blocks outside-click dismissal while something launched from a row owns
   * the screen — a modal opened from a submenu item, say. Without this the
   * click that lands in the modal would close the menu behind it.
   */
  isDismissSuppressed?: boolean
  /**
   * Defaults to 200: above the side nav's own stacking context (100) and
   * below `Modal` (1000), so a modal opened from a row covers the menu.
   */
  zIndex?: number
  minWidth?: number | string
  /**
   * Wraps a row so the consumer can hand it to a router link. Receives the
   * item and the fully-styled row; return the row unchanged to opt out.
   */
  renderItem?: (item: UserMenuItem, row: React.ReactElement) => React.ReactNode
  'data-testid'?: string
  submenuTestId?: string
}

const SUBMENU_Z_INDEX_OFFSET = 10

export const UserMenu = ({
  isOpen,
  onOpen,
  onClose,
  trigger,
  header,
  items,
  colorScheme = 'dark',
  isDismissSuppressed = false,
  zIndex = 200,
  minWidth,
  renderItem,
  'data-testid': dataTestId,
  submenuTestId,
}: UserMenuProps) => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const submenuRef = useRef<HTMLDivElement>(null)
  const [openSubmenuKey, setOpenSubmenuKey] = useState<string | null>(null)

  const menuPos = useAnchoredMenuPosition(isOpen, triggerRef, menuRef, 'bottom')
  // The submenu lines up with the top of the menu that opened it, matching the
  // legacy Help popover's anchoring.
  const submenuPos = useAnchoredMenuPosition(
    openSubmenuKey !== null,
    menuRef,
    submenuRef,
    'top'
  )

  const closeSubmenu = useCallback(() => setOpenSubmenuKey(null), [])

  // A closed menu must not leave its submenu orphaned on screen.
  useEffect(() => {
    if (!isOpen) setOpenSubmenuKey(null)
  }, [isOpen])

  const { onMenuKeyDown, onSubmenuKeyDown } = useUserMenuKeyboard({
    isOpen,
    menuRef,
    onClose,
    onCloseSubmenu: closeSubmenu,
    openSubmenuKey,
    submenuRef,
    triggerRef,
  })

  useEffect(() => {
    if (!isOpen || isDismissSuppressed) return

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node
      // The trigger toggles itself; closing here too would immediately reopen.
      if (triggerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      if (submenuRef.current?.contains(target)) return
      onClose()
    }

    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [isOpen, isDismissSuppressed, onClose])

  const handleToggle = useCallback(() => {
    if (isOpen) {
      onClose()
      return
    }
    onOpen()
  }, [isOpen, onOpen, onClose])

  const handleTriggerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      if (event.currentTarget.tagName === 'BUTTON') return
      // Space would otherwise scroll the page.
      event.preventDefault()
      handleToggle()
    },
    [handleToggle]
  )

  const activateItem = (item: UserMenuItem, key: string) => {
    if (item.submenu?.length) {
      // One submenu open at a time.
      const shouldOpen = openSubmenuKey !== key
      setOpenSubmenuKey(shouldOpen ? key : null)
      // Parent rows carry an onClick too — it is how the consumer tracks that
      // the submenu was reached. Fire it only on the opening half of the
      // toggle, so closing again does not double-count.
      if (shouldOpen) item.onClick?.()
      return
    }
    item.onClick?.()
    if (item.keepOpen) return
    closeSubmenu()
    onClose()
  }

  const renderRow = (item: UserMenuItem) => {
    const key = item.key ?? item.label

    const row = (
      <UserMenuRow
        colorScheme={colorScheme}
        isSubmenuOpen={openSubmenuKey === key}
        item={item}
        onActivate={() => activateItem(item, key)}
      />
    )

    return (
      <React.Fragment key={key}>
        {renderItem ? renderItem(item, row) : row}
      </React.Fragment>
    )
  }

  const activeSubmenu = items.find(
    (item) => (item.key ?? item.label) === openSubmenuKey
  )?.submenu

  const canPortal = typeof document !== 'undefined'
  const shouldShowSubmenu = Boolean(
    isOpen && canPortal && activeSubmenu?.length
  )

  return (
    <>
      {/*
        A real box, not `display: contents` — the position hook measures this
        element, and a contents box has no rect to measure.
      */}
      <Box ref={triggerRef}>
        {trigger({
          'aria-expanded': isOpen,
          'aria-haspopup': 'menu',
          isOpen,
          onClick: handleToggle,
          onKeyDown: handleTriggerKeyDown,
        })}
      </Box>

      {isOpen &&
        canPortal &&
        ReactDOM.createPortal(
          <Box
            {...userMenuSurfaceStyles(colorScheme, minWidth)}
            data-testid={dataTestId}
            onKeyDown={onMenuKeyDown}
            // Faded in rather than `visibility: hidden` so the menu stays in
            // the accessibility tree between the two measurement passes.
            opacity={menuPos?.isMeasured ? 1 : 0}
            pointerEvents={menuPos?.isMeasured ? undefined : 'none'}
            ref={menuRef}
            role='menu'
            transform={`translate3d(${menuPos?.left ?? 0}px, ${
              menuPos?.top ?? 0
            }px, 0)`}
            zIndex={zIndex}
          >
            {header && (
              <>
                <Box {...userMenuHeaderStyles()}>{header}</Box>
                <Box {...userMenuDividerStyles(colorScheme)} />
              </>
            )}
            {items.map(renderRow)}
          </Box>,
          document.body
        )}

      {shouldShowSubmenu &&
        ReactDOM.createPortal(
          <Box
            {...userMenuSurfaceStyles(colorScheme, minWidth)}
            data-testid={submenuTestId}
            onKeyDown={onSubmenuKeyDown}
            opacity={submenuPos?.isMeasured ? 1 : 0}
            pointerEvents={submenuPos?.isMeasured ? undefined : 'none'}
            ref={submenuRef}
            role='menu'
            transform={`translate3d(${submenuPos?.left ?? 0}px, ${
              submenuPos?.top ?? 0
            }px, 0)`}
            zIndex={zIndex + SUBMENU_Z_INDEX_OFFSET}
          >
            {(activeSubmenu ?? []).map(renderRow)}
          </Box>,
          document.body
        )}
    </>
  )
}
